import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, X, Mail, ExternalLink, FileText, Image as ImageIcon, Search, Save, MapPin, CalendarClock, Ruler, Download, Eye, EyeOff, TrendingUp, CalendarCheck, FileSignature, Inbox, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import api from "@/lib/api";
import { IMG } from "@/lib/images";

const PIE_COLORS = ["#1C1C1C", "#A89F91", "#C9C1B4", "#6E7F6A", "#B08968", "#8A8F98", "#7D6E83", "#D4A373", "#5C6B73", "#9C8B7A"];

const MONTHS_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const FOLLOWUP_DAYS = 3;
const needsFollowUp = (c) => (c.status || "Nouveau") === "Nouveau" && (Date.now() - new Date(c.created_at).getTime()) > FOLLOWUP_DAYS * 86400000;


const CONSULTATION_STATUSES = ["Nouveau", "En cours", "Rendez-vous planifié", "Devis envoyé", "Projet signé", "Terminé", "Archivé"];
const STATUS_STYLE = {
  "Nouveau": "bg-blue-50 text-blue-700 border-blue-200",
  "En cours": "bg-amber-50 text-amber-700 border-amber-200",
  "Rendez-vous planifié": "bg-teal-50 text-teal-700 border-teal-200",
  "Devis envoyé": "bg-purple-50 text-purple-700 border-purple-200",
  "Projet signé": "bg-green-50 text-green-700 border-green-200",
  "Terminé": "bg-olime-manifesto text-olime-stone border-olime-line",
  "Archivé": "bg-neutral-100 text-neutral-500 border-neutral-200",
};
const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/consultations/files/${id}?auth=${localStorage.getItem("olime_token")}`;


const emptyArticle = { title: "", category: "Design", date: "", image: "", excerpt: "", content: "" };
const CATEGORIES = ["Design", "Mariage", "Conseils", "Tendances"];
const CONFIG_FIELDS = [
  ["company", "Nom de l'entreprise"], ["founder", "Fondatrice"],
  ["siret", "SIRET"], ["ape", "Code APE"], ["vat", "Mention TVA"],
  ["email", "E-mail"], ["phone", "Téléphone"], ["whatsapp", "WhatsApp (numéro int'l, ex. 33612345678)"], ["city", "Ville / localisation"],
  ["host_name", "Hébergeur"], ["host_address", "Adresse hébergeur"],
  ["instagram", "Instagram (URL)"], ["pinterest", "Pinterest (URL)"], ["linkedin", "LinkedIn (URL)"],
  ["calendly_url", "Lien Calendly (URL) — laisser vide pour masquer le bouton de réservation"],
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { refresh: refreshConfig } = useSiteConfig();
  const [tab, setTab] = useState("consultations");
  const [consultations, setConsultations] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | article
  const [form, setForm] = useState(emptyArticle);
  const [cfg, setCfg] = useState(null);
  const [query, setQuery] = useState("");
  const [notesDraft, setNotesDraft] = useState({}); // id -> notes string
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setEditing(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const load = useCallback(() => {
    api.get("/consultations").then((r) => setConsultations(r.data)).catch(() => {});
    api.get("/consultations/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/journal").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCfgF = (k) => (e) => setCfg((s) => ({ ...s, [k]: e.target.value }));
  const saveConfig = async (e) => {
    e.preventDefault();
    try {
      await api.put("/site-config", cfg);
      await refreshConfig();
      toast.success("Configuration enregistrée");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const deleteConsultation = async (id) => {
    await api.delete(`/consultations/${id}`);
    setConsultations((c) => c.filter((x) => x.id !== id));
    refreshStats();
    toast.success("Demande supprimée");
  };

  const refreshStats = () => { api.get("/consultations/stats").then((r) => setStats(r.data)).catch(() => {}); };

  const updateStatus = async (id, status) => {
    setConsultations((cs) => cs.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/consultations/${id}`, { status });
      refreshStats();
      toast.success("Statut mis à jour");
    } catch { toast.error("Erreur lors de la mise à jour"); }
  };

  const toggleRead = async (id, read) => {
    setConsultations((cs) => cs.map((x) => (x.id === id ? { ...x, read } : x)));
    try {
      await api.patch(`/consultations/${id}`, { read });
      refreshStats();
    } catch { toast.error("Erreur"); }
  };

  const addNote = async (id) => {
    const text = (notesDraft[id] || "").trim();
    if (!text) return;
    try {
      const { data } = await api.post(`/consultations/${id}/notes`, { text });
      setConsultations((cs) => cs.map((x) => (x.id === id ? data : x)));
      setNotesDraft((n) => ({ ...n, [id]: "" }));
      toast.success("Note ajoutée");
    } catch { toast.error("Erreur lors de l'ajout"); }
  };

  const deleteNote = async (id, noteId) => {
    try {
      const { data } = await api.delete(`/consultations/${id}/notes/${noteId}`);
      setConsultations((cs) => cs.map((x) => (x.id === id ? data : x)));
    } catch { toast.error("Erreur"); }
  };

  const unreadCount = consultations.filter((c) => !c.read).length;

  const filteredConsultations = consultations.filter((c) => {
    if (statusFilter === "Non lues") { if (c.read) return false; }
    else if (statusFilter === "À relancer") { if (!needsFollowUp(c)) return false; }
    else if (statusFilter !== "Tous" && (c.status || "Nouveau") !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.project_type, c.project_location, c.email].some((v) => (v || "").toLowerCase().includes(q));
  });

  const exportCsv = () => {
    const cols = ["created_at", "name", "email", "phone", "project_type", "budget", "project_location", "start_date", "surface", "status", "notes", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...consultations.map((c) => cols.map((k) => esc(c[k])).join(","))];
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `olime-demandes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const openNew = () => { setForm(emptyArticle); setEditing({}); };
  const openEdit = (a) => { setForm({ title: a.title, category: a.category, date: a.date, image: a.image, excerpt: a.excerpt, content: a.content }); setEditing(a); };
  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const saveArticle = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Le titre est requis"); return; }
    try {
      if (editing && editing.id) await api.put(`/journal/${editing.id}`, form);
      else await api.post("/journal", form);
      toast.success("Article enregistré");
      setEditing(null);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const deleteArticle = async (id) => {
    await api.delete(`/journal/${id}`);
    setArticles((a) => a.filter((x) => x.id !== id));
    toast.success("Article supprimé");
  };

  return (
    <div className="min-h-screen bg-olime-bg">
      <div className="bg-olime-ink text-white">
        <div className="container-olime py-5 flex items-center justify-between">
          <Link to="/"><img src={IMG.logoWhite} alt="O'Lime" className="h-9 w-auto" /></Link>
          <div className="flex items-center gap-6">
            <span className="text-sm text-white/60 hidden sm:block">{user?.name || user?.email}</span>
            <Link to="/" className="text-xs uppercase tracking-[0.15em] link-underline inline-flex items-center gap-2"><ExternalLink size={13} /> Voir le site</Link>
            <button data-testid="logout-btn" onClick={logout} className="text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 hover:text-olime-sand"><LogOut size={13} /> Déconnexion</button>
          </div>
        </div>
      </div>

      <div className="container-olime py-12">
        <h1 className="font-serif text-5xl mb-8">Tableau de bord</h1>
        <div className="flex gap-3 mb-10 border-b border-olime-line">
          {[["consultations", `Demandes (${consultations.length})`], ["journal", `Journal (${articles.length})`], ["config", "Configuration"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors duration-300 inline-flex items-center gap-2 ${tab === k ? "border-olime-ink text-olime-ink" : "border-transparent text-olime-muted hover:text-olime-ink"}`}>
              {label}
              {k === "consultations" && unreadCount > 0 && (
                <span data-testid="unread-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-olime-ink text-white text-[10px] font-medium">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "consultations" && (
          <div data-testid="consultations-panel" className="space-y-6">
            {stats && (
              <div data-testid="stats-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: "month", icon: Inbox, label: "Demandes ce mois", value: stats.month_count },
                  { key: "appointments", icon: CalendarCheck, label: "Rendez-vous planifiés", value: stats.appointments },
                  { key: "signed", icon: FileSignature, label: "Projets signés", value: stats.signed },
                  { key: "conversion", icon: TrendingUp, label: "Taux de conversion", value: `${stats.conversion_rate}%` },
                ].map((s) => (
                  <div key={s.key} data-testid={`stat-${s.key}`} className="bg-white border border-olime-line p-5">
                    <s.icon size={18} className="text-olime-stone" strokeWidth={1.4} />
                    <p className="font-serif text-4xl mt-3 text-olime-ink">{s.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-olime-muted mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
            {stats && stats.top_service && (
              <p className="text-xs text-olime-muted -mt-2">Service le plus demandé : <span className="text-olime-ink">{stats.top_service}</span> · {stats.total} demande{stats.total > 1 ? "s" : ""} au total</p>
            )}

            {consultations.filter(needsFollowUp).length > 0 && (
              <button data-testid="followup-alert" onClick={() => setStatusFilter("À relancer")}
                className="w-full flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-5 py-3.5 text-sm hover:bg-amber-100 transition-colors text-left">
                <AlertTriangle size={16} className="shrink-0" />
                <span><strong>{consultations.filter(needsFollowUp).length}</strong> demande{consultations.filter(needsFollowUp).length > 1 ? "s" : ""} en attente de réponse depuis plus de {FOLLOWUP_DAYS} jours — cliquez pour les afficher.</span>
              </button>
            )}

            {stats && stats.by_month && stats.total > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div data-testid="monthly-chart" className="bg-white border border-olime-line p-6 lg:col-span-2">
                  <p className="eyebrow mb-4">Demandes des 6 derniers mois</p>
                  <div style={{ width: "100%", height: 180 }}>
                    <ResponsiveContainer>
                      <BarChart data={stats.by_month.map((m) => ({ name: MONTHS_FR[parseInt(m.month.slice(5), 10) - 1], count: m.count }))} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#A89F91" }} />
                        <Tooltip cursor={{ fill: "rgba(168,159,145,0.08)" }} contentStyle={{ border: "1px solid #EFEBE6", fontSize: 12, borderRadius: 0 }} labelStyle={{ color: "#1C1C1C" }} />
                        <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={48}>
                          {stats.by_month.map((_, i) => <Cell key={i} fill={i === stats.by_month.length - 1 ? "#1C1C1C" : "#C9C1B4"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div data-testid="service-chart" className="bg-white border border-olime-line p-6">
                  <p className="eyebrow mb-4">Répartition par service</p>
                  <div style={{ width: "100%", height: 180 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={Object.entries(stats.by_service).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" innerRadius={38} outerRadius={70} paddingAngle={2}>
                          {Object.keys(stats.by_service).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ border: "1px solid #EFEBE6", fontSize: 12, borderRadius: 0 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative max-w-md flex-1">
                <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-olime-muted" />
                <input data-testid="consultation-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher par nom ou par projet…" className="field-line pl-7 w-full" />
              </div>
              <button data-testid="export-csv" onClick={exportCsv} disabled={consultations.length === 0} className="btn-outline !px-4 !py-2.5 text-[10px] disabled:opacity-40 shrink-0"><Download size={13} /> Exporter en CSV</button>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="status-filters">
              {["Tous", "Non lues", "À relancer", ...CONSULTATION_STATUSES].map((s) => {
                const count = s === "Non lues" ? unreadCount : s === "À relancer" ? consultations.filter(needsFollowUp).length : null;
                return (
                  <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
                    className={`text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${statusFilter === s ? "bg-olime-ink text-white border-olime-ink" : "border-olime-line text-olime-muted hover:border-olime-ink hover:text-olime-ink"}`}>
                    {s}{count ? <span className={statusFilter === s ? "text-white/70" : "text-olime-stone"}>({count})</span> : null}
                  </button>
                );
              })}
            </div>

            {filteredConsultations.length === 0 && <p className="text-olime-muted">Aucune demande{query || statusFilter !== "Tous" ? " pour ce filtre" : " pour le moment"}.</p>}
            {filteredConsultations.map((c) => (
              <div key={c.id} data-testid={`consultation-${c.id}`} className={`bg-white border p-6 ${c.read ? "border-olime-line" : "border-l-4 border-l-olime-ink border-y-olime-line border-r-olime-line"}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      {!c.read && <span data-testid={`unread-dot-${c.id}`} className="w-2 h-2 rounded-full bg-olime-ink" title="Non lue" />}
                      <h3 className="font-serif text-2xl">{c.name}</h3>
                      {c.project_type && <span className="text-[10px] uppercase tracking-[0.15em] bg-olime-manifesto px-2 py-1">{c.project_type}</span>}
                      {c.budget && <span className="text-xs text-olime-stone">{c.budget}</span>}
                      {needsFollowUp(c) && <span data-testid={`followup-badge-${c.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1"><AlertTriangle size={11} /> À relancer</span>}
                    </div>
                    <p className="text-sm text-olime-muted inline-flex items-center gap-2"><Mail size={13} /> {c.email} {c.phone && `• ${c.phone}`}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-olime-muted mt-1">
                      {c.project_location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.project_location}</span>}
                      {c.start_date && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {c.start_date}</span>}
                      {c.surface && <span className="inline-flex items-center gap-1"><Ruler size={12} /> {c.surface}</span>}
                    </div>
                    {c.message && <p className="text-sm text-olime-ink mt-2 max-w-2xl whitespace-pre-line">{c.message}</p>}
                    <p className="text-xs text-olime-muted mt-1">{new Date(c.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button data-testid={`toggle-read-${c.id}`} onClick={() => toggleRead(c.id, !c.read)} title={c.read ? "Marquer comme non lue" : "Marquer comme lue"} className="text-olime-muted hover:text-olime-ink transition-colors">
                      {c.read ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                    <a href={`mailto:${c.email}`} className="btn-outline !px-4 !py-2 text-[10px]">Répondre</a>
                    <button data-testid={`delete-consultation-${c.id}`} onClick={() => deleteConsultation(c.id)} className="text-olime-muted hover:text-red-600 transition-colors"><Trash2 size={18} /></button>
                  </div>
                </div>

                {c.files && c.files.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid={`consultation-files-${c.id}`}>
                    {c.files.map((file) => (
                      <a key={file.id} href={fileUrl(file.id)} target="_blank" rel="noopener noreferrer" data-testid={`file-link-${file.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-olime-bg border border-olime-line hover:border-olime-ink transition-colors">
                        {file.content_type === "application/pdf" ? <FileText size={13} className="text-olime-stone" /> : <ImageIcon size={13} className="text-olime-stone" />}
                        <span className="truncate max-w-[180px]">{file.original_filename}</span>
                      </a>
                    ))}
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-olime-line grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="eyebrow">Statut</label>
                    <div className="flex items-center gap-3 mt-2">
                      <select data-testid={`status-select-${c.id}`} value={c.status || "Nouveau"} onChange={(e) => updateStatus(c.id, e.target.value)}
                        className={`text-xs uppercase tracking-[0.1em] border px-3 py-2 ${STATUS_STYLE[c.status] || STATUS_STYLE.Nouveau}`}>
                        {CONSULTATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="eyebrow">Notes internes</label>
                    <div className="mt-2 flex gap-2">
                      <textarea data-testid={`notes-${c.id}`} rows={2}
                        value={notesDraft[c.id] || ""}
                        onChange={(e) => setNotesDraft((n) => ({ ...n, [c.id]: e.target.value }))}
                        placeholder="Ajouter une note datée…" className="field-line resize-none flex-1" />
                      <button data-testid={`add-note-${c.id}`} onClick={() => addNote(c.id)} className="btn-ink !px-4 self-start" title="Ajouter la note"><Plus size={14} /></button>
                    </div>
                    {(c.notes_entries && c.notes_entries.length > 0) && (
                      <ul className="mt-3 space-y-2" data-testid={`notes-log-${c.id}`}>
                        {[...c.notes_entries].reverse().map((n) => (
                          <li key={n.id} className="group flex items-start justify-between gap-3 bg-olime-bg border border-olime-line px-3 py-2 text-sm">
                            <div className="min-w-0">
                              <p className="text-olime-ink whitespace-pre-line break-words">{n.text}</p>
                              <p className="text-[11px] text-olime-muted mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                            </div>
                            <button data-testid={`delete-note-${n.id}`} onClick={() => deleteNote(c.id, n.id)} className="shrink-0 text-olime-muted hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "journal" && (
          <div data-testid="journal-panel">
            <button data-testid="new-article-btn" onClick={openNew} className="btn-ink mb-8"><Plus size={14} /> Nouvel article</button>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a) => (
                <div key={a.id} data-testid={`article-${a.slug}`} className="bg-white border border-olime-line overflow-hidden">
                  {a.image && <img src={a.image} alt={a.title} className="w-full aspect-[16/10] object-cover" />}
                  <div className="p-5">
                    <p className="eyebrow">{a.category} • {a.date}</p>
                    <h3 className="font-serif text-xl mt-2 leading-snug">{a.title}</h3>
                    <div className="flex gap-3 mt-4">
                      <button data-testid={`edit-article-${a.slug}`} onClick={() => openEdit(a)} className="text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1 link-underline"><Pencil size={13} /> Éditer</button>
                      <button data-testid={`delete-article-${a.slug}`} onClick={() => deleteArticle(a.id)} className="text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1 text-olime-muted hover:text-red-600"><Trash2 size={13} /> Supprimer</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {tab === "config" && (
        <div data-testid="config-panel" className="container-olime pb-12 -mt-4 max-w-3xl">
          <p className="text-olime-muted mb-8 text-sm">Ces informations alimentent le pied de page et les pages légales (mentions légales, confidentialité…). Elles sont modifiables ici sans toucher au code.</p>
          {cfg ? (
            <form onSubmit={saveConfig} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {CONFIG_FIELDS.map(([k, label]) => (
                <div key={k} className={k === "vat" || k === "host_address" || k === "calendly_url" ? "md:col-span-2" : ""}>
                  <label className="eyebrow">{label}</label>
                  <input data-testid={`config-${k}`} value={cfg[k] || ""} onChange={setCfgF(k)} className="field-line mt-2" />
                  {k === "calendly_url" && (cfg.calendly_url || "").trim() && (
                    <a href={cfg.calendly_url} target="_blank" rel="noopener noreferrer" data-testid="calendly-preview-link"
                      className="mt-2 inline-flex items-center gap-2 text-xs text-olime-stone hover:text-olime-ink link-underline">
                      <ExternalLink size={12} /> Tester le lien Calendly
                    </a>
                  )}
                </div>
              ))}
              <div className="md:col-span-2">
                <button type="submit" data-testid="save-config" className="btn-ink">Enregistrer la configuration</button>
              </div>
            </form>
          ) : <p className="text-olime-muted">Chargement…</p>}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div data-testid="article-modal" className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 hide-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl">{editing.id ? "Éditer l'article" : "Nouvel article"}</h2>
              <button onClick={() => setEditing(null)}><X size={22} /></button>
            </div>
            <form onSubmit={saveArticle} className="space-y-5">
              <div><label className="eyebrow">Titre</label><input data-testid="article-title" value={form.title} onChange={setF("title")} className="field-line mt-2" /></div>
              <div className="grid grid-cols-2 gap-5">
                <div><label className="eyebrow">Catégorie</label>
                  <select data-testid="article-category" value={form.category} onChange={setF("category")} className="field-line mt-2">
                    {CATEGORIES.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
                  </select>
                </div>
                <div><label className="eyebrow">Date</label><input data-testid="article-date" value={form.date} onChange={setF("date")} className="field-line mt-2" placeholder="Mars 2024" /></div>
              </div>
              <div><label className="eyebrow">Image (URL)</label><input data-testid="article-image" value={form.image} onChange={setF("image")} className="field-line mt-2" placeholder="https://..." /></div>
              <div><label className="eyebrow">Extrait</label><textarea data-testid="article-excerpt" value={form.excerpt} onChange={setF("excerpt")} rows={2} className="field-line mt-2 resize-none" /></div>
              <div><label className="eyebrow">Contenu</label><textarea data-testid="article-content" value={form.content} onChange={setF("content")} rows={6} className="field-line mt-2 resize-none" /></div>
              <button type="submit" data-testid="save-article" className="btn-ink w-full">Enregistrer</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
